// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import {
  abbreviateCaseBuddyHash,
  firstNonEmptyEntryValue,
  normalizeCell,
  normalizeComparisonKey,
  parseCaseUtcDate,
  rowHasMeaningfulData
} from './CaseBuddyShared.js';
import { CASE_COLUMN_HEADERS, CASE_DEFAULT_COLS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function parseCaseEntries(data = []) {
  const entries = [];
  let current = null;

  (Array.isArray(data) ? data : []).forEach((row, rowIndex) => {
    if (rowIndex === 0) return;
    if (!rowHasMeaningfulData(row)) return;

    if (rowLooksLikeCaseBuddyHeader(row)) {
      current = null;
      return;
    }

    const hash = normalizeCell(row?.[1]);
    if (hash) {
      current = {
        hash,
        startRow: rowIndex,
        rows: [{ rowIndex, row }]
      };
      entries.push(current);
      return;
    }

    if (current) {
      current.rows.push({ rowIndex, row });
    }
  });

  return entries;
}

export function isLikelyPastedHeaderCell(value) {
  const normalized = normalizeCell(value).toLowerCase();
  return ['transaction hash', 'txn hash', 'hash', 'to address', 'to', 'destination address', 'destination'].includes(normalized);
}

export function isLikelyStartingTransactionHash(value) {
  const text = normalizeCell(value);
  if (!text || isLikelyPastedHeaderCell(text)) return false;
  if (/\s/.test(text)) return false;
  return text.length >= 6;
}

export function isMainTransactionPathLabel(value) {
  const label = normalizeCell(value);
  if (!label) return false;
  if (label.includes('->') || label.includes('→') || label.includes('.') || /\s/.test(label)) return false;

  if (/^\d+$/.test(label)) return true;
  if (/^[A-Z]+$/.test(label)) return true;
  if (/^[A-Za-z0-9][A-Za-z0-9_:/.-]*-\d+$/.test(label)) return true;

  return false;
}

export function isStandardNumericMainPathLabel(value) {
  const label = normalizeCell(value);
  return /^\d+$/.test(label) && Number.parseInt(label, 10) > 0;
}

export function normalizeNumericMainPathLabel(value) {
  const label = normalizeCell(value);
  if (!isStandardNumericMainPathLabel(label)) return '';
  return String(Number.parseInt(label, 10));
}

export function rowHasFromHash(row = []) {
  return !!normalizeCell(Array.isArray(row) ? row[14] : '');
}

export function buildTopLevelMainEntryDescriptors(data = []) {
  const normalizedData = normalizeCaseMatrix(data);
  const entries = parseCaseEntries(normalizedData);

  return entries
    .filter(entry => entry && entry.hash && Array.isArray(entry.rows) && entry.rows.length > 0)
    .filter(entry => !entry.rows.some(entryRow => rowHasFromHash(entryRow?.row)))
    .map((entry, index) => {
      const lead = entry.rows[0] || { rowIndex: entry.startRow, row: [] };
      const leadRow = Array.isArray(lead.row) ? lead.row : [];
      const dateInfo = parseCaseUtcDate(firstNonEmptyEntryValue(entry.rows, 4) || leadRow[4]);
      const toAddress = normalizeCell(leadRow[3]) || firstNonEmptyEntryValue(entry.rows, 3);
      const currentLabel = normalizeCell(leadRow[0]);
      const continuationLabelRows = entry.rows
        .slice(1)
        .filter(entryRow => rowHasMeaningfulData(entryRow?.row) && normalizeCell(entryRow?.row?.[0]))
        .map(entryRow => entryRow.rowIndex);

      return {
        id: `top-main-entry-${lead.rowIndex}-${index}-${normalizeComparisonKey(entry.hash) || 'blank'}`,
        originalOrder: index,
        startRow: lead.rowIndex,
        spreadsheetRowNumber: lead.rowIndex + 1,
        hash: normalizeCell(entry.hash || leadRow[1]),
        currentLabel,
        dateInfo,
        utcDate: dateInfo.isValid ? dateInfo.canonical : '',
        toAddress,
        entryRows: entry.rows.map(entryRow => ({
          rowIndex: entryRow.rowIndex,
          row: Array.isArray(entryRow.row) ? entryRow.row : []
        })),
        continuationLabelRows
      };
    });
}

export function sortTopLevelMainEntriesForLabelPlan(entries = []) {
  return (Array.isArray(entries) ? entries : []).slice().sort((left, right) => {
    const leftValid = !!left.dateInfo?.isValid;
    const rightValid = !!right.dateInfo?.isValid;
    if (leftValid && rightValid) {
      if (left.dateInfo.timestamp !== right.dateInfo.timestamp) {
        return left.dateInfo.timestamp - right.dateInfo.timestamp;
      }
      return left.startRow - right.startRow;
    }
    if (leftValid && !rightValid) return -1;
    if (!leftValid && rightValid) return 1;
    return left.startRow - right.startRow;
  });
}

export function analyzeNumericMainPathLabels(data = []) {
  const entries = sortTopLevelMainEntriesForLabelPlan(buildTopLevelMainEntryDescriptors(data));
  let completeCount = 0;
  let missingCount = 0;
  let invalidCount = 0;
  let sequenceIssueCount = 0;
  let missingDateCount = 0;
  let continuationLabelCount = 0;
  const examples = [];

  entries.forEach((entry, index) => {
    const expected = String(index + 1);
    const label = normalizeNumericMainPathLabel(entry.currentLabel);
    const hasDate = !!entry.dateInfo?.isValid;
    const hasContinuationLabels = entry.continuationLabelRows.length > 0;
    const ok = hasDate && label && label === expected && !hasContinuationLabels;

    if (ok) {
      completeCount += 1;
      return;
    }

    if (!hasDate) {
      missingDateCount += 1;
    }

    if (!entry.currentLabel) {
      missingCount += 1;
    } else if (!label) {
      invalidCount += 1;
    } else if (label !== expected) {
      sequenceIssueCount += 1;
    }

    if (hasContinuationLabels) {
      continuationLabelCount += entry.continuationLabelRows.length;
    }

    if (examples.length < 3) {
      examples.push({
        hash: entry.hash,
        row: entry.spreadsheetRowNumber,
        current: entry.currentLabel,
        expected,
        hasContinuationLabels
      });
    }
  });

  return {
    entries,
    total: entries.length,
    completeCount,
    missingCount,
    invalidCount,
    sequenceIssueCount,
    missingDateCount,
    continuationLabelCount,
    examples,
    pass: entries.length > 0 && missingDateCount === 0 && completeCount === entries.length
  };
}

export function getUsedColumnIndexes(rows = []) {
  const used = new Set();
  rows.forEach(row => {
    if (!Array.isArray(row)) return;
    row.forEach((cell, index) => {
      if (normalizeCell(cell)) used.add(index);
    });
  });
  return used;
}

export function isColumnSetSubset(sourceSet, allowedIndexes) {
  const allowed = new Set(allowedIndexes);
  return Array.from(sourceSet).every(index => allowed.has(index));
}

export function analyzeStartingMainTransactions(data = []) {
  const rows = (Array.isArray(data) ? data : []).slice(1).map((row, offset) => ({
    rowIndex: offset + 1,
    row: Array.isArray(row) ? row : []
  }));
  const meaningfulRows = rows.filter(({ row }) => rowHasMeaningfulData(row));
  const usedColumns = getUsedColumnIndexes(meaningfulRows.map(({ row }) => row));
  const hashRows = meaningfulRows.filter(({ row }) => normalizeCell(row?.[1]));
  const toOnlyRows = meaningfulRows.filter(({ row }) => !normalizeCell(row?.[1]) && normalizeCell(row?.[3]));
  const hasPathLabelData = meaningfulRows.some(({ row }) => normalizeCell(row?.[0]));
  const onlyStartingColumns = isColumnSetSubset(usedColumns, [1, 3]);
  const onlyPrePathColumns = isColumnSetSubset(usedColumns, [1, 3, 4, 19, 20]);
  const mainLabelRows = hashRows.filter(({ row }) => isMainTransactionPathLabel(row?.[0]));
  const unlabeledTopLevelRows = hashRows.filter(({ row }) => !normalizeCell(row?.[0]) && !normalizeCell(row?.[14]));

  let mode = 'advanced';
  let mainTransactionRows = [];
  let trashRows = [];

  if (meaningfulRows.length === 0) {
    mode = 'empty';
  } else if (!hasPathLabelData && onlyStartingColumns) {
    mode = 'starting-entry';
    mainTransactionRows = hashRows;
    trashRows = toOnlyRows;
  } else if (!hasPathLabelData && onlyPrePathColumns) {
    mode = 'pre-path-label';
    mainTransactionRows = hashRows;
    trashRows = toOnlyRows;
  } else if (mainLabelRows.length > 0) {
    mode = 'path-labeled';
    mainTransactionRows = mainLabelRows;
  } else if (unlabeledTopLevelRows.length > 0) {
    mode = 'advanced-unlabeled';
    mainTransactionRows = unlabeledTopLevelRows;
  } else {
    mainTransactionRows = hashRows;
  }

  return {
    mode,
    meaningfulRows,
    usedColumns: Array.from(usedColumns).sort((a, b) => a - b),
    hashRows,
    toOnlyRows,
    trashRows,
    mainLabelRows,
    unlabeledTopLevelRows,
    mainTransactionRows,
    mainTransactionCount: mainTransactionRows.length,
    trashRowCount: trashRows.length
  };
}

export function normalizeCaseRowsToWidth(rows = [], minCols = CASE_DEFAULT_COLS) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const source = Array.isArray(row) ? row.slice() : [];
    const targetColCount = Math.max(minCols, source.length);
    return source.length >= targetColCount
      ? source
      : [...source, ...Array(targetColCount - source.length).fill('')];
  });
}

export function trimOuterEmptyRows(rows = []) {
  const source = Array.isArray(rows) ? rows : [];
  let start = 0;
  let end = source.length - 1;

  while (start <= end && !rowHasMeaningfulData(source[start])) start += 1;
  while (end >= start && !rowHasMeaningfulData(source[end])) end -= 1;

  return start <= end ? source.slice(start, end + 1) : [];
}

export function parseTsvRows(text = '') {
  const normalizedText = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalizedText.trim()) return [];

  return trimOuterEmptyRows(normalizedText.split('\n').map(line => line.split('\t')));
}

export function rowLooksLikeCaseBuddyHeader(row = []) {
  const colA = normalizeCell(row?.[0]).toLowerCase();
  const colB = normalizeCell(row?.[1]).toLowerCase();
  const colC = normalizeCell(row?.[2]).toLowerCase();
  const colD = normalizeCell(row?.[3]).toLowerCase();
  const colE = normalizeCell(row?.[4]).toLowerCase();
  const colO = normalizeCell(row?.[14]).toLowerCase();
  const colT = normalizeCell(row?.[19]).toLowerCase();
  const colU = normalizeCell(row?.[20]).toLowerCase();

  const hashHeader = (colB.includes('transaction') && colB.includes('hash')) || colB === 'hash' || colB === 'txn hash';
  if (!hashHeader) return false;

  const hits = [
    colA.includes('path') && colA.includes('label'),
    colC.includes('from'),
    colD.includes('to') || colD.includes('destination'),
    colE.includes('date') || colE.includes('utc'),
    colO.includes('from') && colO.includes('hash'),
    colT.includes('chain') && colT.includes('id'),
    colU.includes('chain') && (colU.includes('name') || colU.includes('network'))
  ].filter(Boolean).length;

  return hits >= 1;
}

export function normalizeRawDataImportRowsFromPaste(text = '', options = {}) {
  const addHeadersIfMissing = options.addHeadersIfMissing !== false;
  const parsedRows = normalizeCaseRowsToWidth(parseTsvRows(text));
  if (parsedRows.length === 0) return [];

  const firstMeaningfulIndex = parsedRows.findIndex(rowHasMeaningfulData);
  if (firstMeaningfulIndex < 0) return [];

  const firstMeaningfulRow = parsedRows[firstMeaningfulIndex];
  if (rowLooksLikeCaseBuddyHeader(firstMeaningfulRow) || !addHeadersIfMissing) {
    return parsedRows;
  }

  return [CASE_COLUMN_HEADERS.slice(), ...parsedRows];
}

export function findLastMeaningfulRowIndex(rows = []) {
  let lastIndex = -1;
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    if (rowHasMeaningfulData(row)) lastIndex = index;
  });
  return lastIndex;
}

export function firstNonEmptyRowsValue(rows = [], columnIndex) {
  for (const row of Array.isArray(rows) ? rows : []) {
    const value = normalizeCell(row?.[columnIndex]);
    if (value) return value;
  }
  return '';
}

export function isRawRootCandidateMatrixRow(row = []) {
  return !!normalizeCell(row?.[1]) && !normalizeCell(row?.[14]) && !rowLooksLikeCaseBuddyHeader(row);
}

export function buildRawDataSetsFromMatrix(data = []) {
  const rows = normalizeCaseRowsToWidth(Array.isArray(data) ? data : []);
  const meaningfulIndexes = rows
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => rowHasMeaningfulData(row))
    .map(({ rowIndex }) => rowIndex);

  if (meaningfulIndexes.length === 0) return [];

  const headerIndexes = [];
  rows.forEach((row, rowIndex) => {
    if (rowLooksLikeCaseBuddyHeader(row)) {
      headerIndexes.push(rowIndex);
    }
  });

  const intervalStarts = headerIndexes.length > 0 ? headerIndexes : [meaningfulIndexes[0]];
  const intervals = intervalStarts.map((startRowIndex, intervalIndex) => ({
    startRowIndex,
    endExclusive: intervalStarts[intervalIndex + 1] ?? rows.length,
    hasHeader: rowLooksLikeCaseBuddyHeader(rows[startRowIndex]),
    headerRowIndex: rowLooksLikeCaseBuddyHeader(rows[startRowIndex]) ? startRowIndex : null
  }));

  const sets = [];

  intervals.forEach((interval) => {
    const rootRowIndexes = [];
    for (let rowIndex = interval.startRowIndex + (interval.hasHeader ? 1 : 0); rowIndex < interval.endExclusive; rowIndex += 1) {
      const row = rows[rowIndex];
      if (!rowHasMeaningfulData(row) || rowLooksLikeCaseBuddyHeader(row)) continue;
      if (isRawRootCandidateMatrixRow(row)) rootRowIndexes.push(rowIndex);
    }

    rootRowIndexes.forEach((rootRowIndex, rootOffset) => {
      const nextRootRowIndex = rootRowIndexes[rootOffset + 1] ?? interval.endExclusive;
      const setStartRowIndex = rootOffset === 0 && interval.hasHeader ? interval.headerRowIndex : rootRowIndex;
      const setHeaderRowIndex = rootOffset === 0 && interval.hasHeader ? interval.headerRowIndex : null;
      let entryStopRowIndex = nextRootRowIndex;

      for (let rowIndex = rootRowIndex + 1; rowIndex < nextRootRowIndex; rowIndex += 1) {
        const row = rows[rowIndex];
        if (rowLooksLikeCaseBuddyHeader(row) || normalizeCell(row?.[1])) {
          entryStopRowIndex = rowIndex;
          break;
        }
      }

      const rootEntryRows = rows.slice(rootRowIndex, entryStopRowIndex).filter(rowHasMeaningfulData);
      const setRows = rows.slice(setStartRowIndex, nextRootRowIndex);
      const lastMeaningfulRelativeIndex = findLastMeaningfulRowIndex(setRows);
      const endRowIndex = lastMeaningfulRelativeIndex >= 0 ? setStartRowIndex + lastMeaningfulRelativeIndex : rootRowIndex;
      const rootRow = rows[rootRowIndex] || [];
      const rootHash = normalizeCell(rootRow?.[1]);
      if (!rootHash) return;

      const rootToAddress = normalizeCell(rootRow?.[3]) || firstNonEmptyRowsValue(rootEntryRows, 3);

      sets.push({
        id: `raw-set-${setHeaderRowIndex ?? 'noheader'}-${rootRowIndex}-${normalizeComparisonKey(rootHash) || 'blank'}`,
        setIndex: sets.length,
        headerRowIndex: setHeaderRowIndex,
        rootRowIndex,
        endRowIndex,
        displayHeaderRow: Number.isInteger(setHeaderRowIndex) ? setHeaderRowIndex + 1 : '',
        displayRootRow: rootRowIndex + 1,
        rootHash,
        rootHashKey: normalizeComparisonKey(rootHash),
        rootToAddress,
        rootToAddressKey: normalizeComparisonKey(rootToAddress),
        rowCount: Math.max(0, endRowIndex - setStartRowIndex + 1),
        rootRow: rootRow.slice(),
        rows: rows.slice(setStartRowIndex, endRowIndex + 1)
      });
    });
  });

  return sets;
}

export function buildRawDataAppendMatrix(existingData = [], rowsToAppend = []) {
  const data = normalizeCaseMatrix(existingData);
  const rows = normalizeCaseRowsToWidth(rowsToAppend).filter((row, index, source) => {
    if (rowHasMeaningfulData(row)) return true;
    const previousMeaningful = index > 0 && rowHasMeaningfulData(source[index - 1]);
    const nextMeaningful = index < source.length - 1 && rowHasMeaningfulData(source[index + 1]);
    return previousMeaningful && nextMeaningful;
  });

  if (rows.length === 0) {
    return { data, startRow: -1, rowsAppended: 0, spacerRowInserted: false };
  }

  const lastMeaningfulRow = findLastMeaningfulRowIndex(data);
  const hasOnlyHeader = rowLooksLikeCaseBuddyHeader(data[0]) && data.slice(1).every(row => !rowHasMeaningfulData(row));
  let startRow = 0;
  let spacerRowInserted = false;

  if (hasOnlyHeader) {
    startRow = rowLooksLikeCaseBuddyHeader(rows[0]) ? 0 : 1;
  } else if (lastMeaningfulRow >= 0) {
    startRow = Math.max(1, lastMeaningfulRow + 2);
    spacerRowInserted = true;
  }

  const neededRows = startRow + rows.length;
  while (data.length < neededRows) {
    data.push(Array(CASE_DEFAULT_COLS).fill(''));
  }

  rows.forEach((row, offset) => {
    data[startRow + offset] = normalizeCaseRowsToWidth([row])[0];
  });

  return { data, startRow, rowsAppended: rows.length, spacerRowInserted };
}

export function buildMainRawDataCoverage(mainData = [], rawData = []) {
  const normalizedMainData = normalizeCaseMatrix(mainData);
  const mainTransactionAnalysis = analyzeStartingMainTransactions(normalizedMainData);
  const mainEntriesByStartRow = new Map(parseCaseEntries(normalizedMainData).map(entry => [entry.startRow, entry]));
  const rawSets = buildRawDataSetsFromMatrix(rawData);

  const coverageRows = mainTransactionAnalysis.mainTransactionRows.map(({ rowIndex, row }, index) => {
    const entry = mainEntriesByStartRow.get(rowIndex) || {
      startRow: rowIndex,
      rows: [{ rowIndex, row }]
    };
    const entryRows = Array.isArray(entry.rows) && entry.rows.length > 0
      ? entry.rows
      : [{ rowIndex, row }];
    const leadRow = entryRows[0]?.row || row || [];
    const pathLabel = normalizeCell(leadRow?.[0]) || firstNonEmptyEntryValue(entryRows, 0);
    const hash = normalizeCell(leadRow?.[1] || entry.hash || row?.[1]);
    const toAddress = normalizeCell(leadRow?.[3]) || firstNonEmptyEntryValue(entryRows, 3);
    const hashKey = normalizeComparisonKey(hash);
    const toAddressKey = normalizeComparisonKey(toAddress);
    const hashMatchedSets = rawSets.filter(set => set.rootHashKey === hashKey);
    const matchingSets = hashMatchedSets.filter(set => !toAddressKey || set.rootToAddressKey === toAddressKey);
    const bestSet = matchingSets.slice().sort((a, b) => b.rootRowIndex - a.rootRowIndex)[0] || null;
    const status = bestSet ? 'covered' : hashMatchedSets.length > 0 ? 'to-mismatch' : 'missing';
    const statusLabel = bestSet ? 'Covered' : status === 'to-mismatch' ? 'Hash found, To mismatch' : 'Missing raw data';
    const message = bestSet
      ? `Using raw set rooted at Raw Data row ${bestSet.displayRootRow}. ${matchingSets.length > 1 ? 'A lower matching set was selected.' : ''}`.trim()
      : status === 'to-mismatch'
        ? 'A raw root with this hash exists, but its Column D To Address does not match the main transaction.'
        : 'No raw root set found for this main transaction hash.';

    return {
      id: `raw-coverage-${rowIndex}-${index}-${hashKey || 'blank'}-${toAddressKey || 'any-to'}`,
      rowIndex,
      spreadsheetRowNumber: rowIndex + 1,
      pathLabel,
      hash,
      shortHash: abbreviateCaseBuddyHash(hash),
      toAddress,
      shortToAddress: abbreviateCaseBuddyHash(toAddress),
      requiresToAddressMatch: !!toAddressKey,
      status,
      statusLabel,
      message,
      matchedSet: bestSet,
      matchingSetCount: matchingSets.length,
      hashMatchedSetCount: hashMatchedSets.length,
      matchedRawRootRowNumber: bestSet?.displayRootRow || '',
      matchedRawSetStartRowNumber: bestSet?.displayHeaderRow || ''
    };
  });

  const coveredCount = coverageRows.filter(row => row.status === 'covered').length;
  const missingRows = coverageRows.filter(row => row.status !== 'covered');
  const mismatchCount = coverageRows.filter(row => row.status === 'to-mismatch').length;
  const missingCount = missingRows.length;
  const totalMainTransactions = coverageRows.length;
  const ready = mainTransactionAnalysis.mainTransactionCount > 0 && mainTransactionAnalysis.trashRowCount === 0 && missingCount === 0;

  return {
    mainTransactionAnalysis,
    rawSets,
    coverageRows,
    rows: coverageRows,
    coveredCount,
    covered: coveredCount,
    matchedCount: coveredCount,
    missingRows,
    missingCount,
    missing: missingCount,
    mismatchCount,
    mismatched: mismatchCount,
    toMismatchCount: mismatchCount,
    totalMainTransactions,
    totalRequired: totalMainTransactions,
    total: totalMainTransactions,
    ready,
    complete: ready,
    allCovered: ready
  };
}

export function buildRawDataImportAuditRows(importResult = {}, coverage = {}) {
  const coverageRows = Array.isArray(coverage.coverageRows) ? coverage.coverageRows : [];
  return [
    ['Case Buddy Raw Data Import Audit', '', '', '', '', '', '', '', '', 'Generated by Load Raw Data For Main Transactions. Do not append this audit back into Main Data.'],
    ['Imported Rows', importResult.rowsAppended || 0, 'Raw Sets In Sheet', coverage.rawSets?.length || 0, 'Covered Main Txns', coverage.coveredCount || 0, 'Missing Main Txns', coverage.missingCount || 0],
    ['Raw Start Row', importResult.startRow >= 0 ? importResult.startRow + 1 : '', 'Spacer Inserted', importResult.spacerRowInserted ? 'Yes' : 'No', 'Coverage Complete', coverage.ready ? 'Yes' : 'No'],
    ['Main Row', 'Path Label', 'Transaction Hash', 'To Address', 'Coverage Status', 'Raw Root Row', 'Raw To Address', 'Message'],
    ...coverageRows.map(row => [
      row.spreadsheetRowNumber,
      row.pathLabel || '',
      row.hash || '',
      row.toAddress || '',
      row.statusLabel || '',
      row.matchedSet?.displayRootRow || '',
      row.matchedSet?.rootToAddress || '',
      row.message || ''
    ])
  ];
}
